import { forceUpdate, h, Host } from "@stencil/core";
import { ACTION_MENU_ITEM_PARTS_DICTIONARY, ACTION_MENU_PARTS_DICTIONARY, KEY_CODES } from "../../common/reserved-names";
import { fromGxImageToURL } from "../tree-view/genexus-implementation";
import { tokenMap } from "../../common/utils";
import { actionMenuKeyEventsDictionary } from "./internal/keyboard-actions";
import { parseSubModel } from "./internal/parse-model";
import { collapseAllItems, collapseSubTree, expandFromRootToNode } from "./internal/update-expanded";
import { ACTION_MENU_RENDER_TAG_NAME, actionMenuItemActionableIsExpandable, actionMenuItemIsActionable, actionMenuItemIsHyperlink, getActionMenuInfoInEvent, WINDOW_ID } from "./internal/utils";
export class ChActionMenuRender {
    constructor() {
        this.buttonAccessibleName = undefined;
        this.blockAlign = "outside-end";
        this.disabled = false;
        this.getImagePathCallback = undefined;
        this.expanded = false;
        this.gxImageConstructor = undefined;
        this.gxSettings = undefined;
        this.inlineAlign = "center";
        this.model = undefined;
        this.positionTry = "none";
        this.useGxRender = false;
    }
    // Refs
    #actionRef;
    #popoverRef;
    el;
    expandedChanged() {
        if (this.expanded) {
            this.#addCloseOnClickOutside();
        }
        else {
            this.#removeCloseOnClickOutside();
        }
    }
    modelChanged() {
        this.#addMetadataToItems();
    }
    /**
     * Fired when a button is clicked.
     * This event can be prevented.
     */
    buttonClick;
    /**
     * Fired when the visibility of the main dropdown is changed.
     */
    expandedChange;
    /**
     * Fired when the visibility of a dropdown item is changed.
     */
    expandedItemChange;
    /**
     * Fired when an hyperlink is clicked.
     * This event can be prevented, but the dropdown will be closed in any case
     * (prevented or not).
     */
    hyperlinkClick;
    #renderActionItem = (itemUIModel) => {
        const expandable = actionMenuItemActionableIsExpandable(itemUIModel);
        return (h("ch-action-menu", { blockAlign: itemUIModel.itemsBlockAlign ?? "inside-start", caption: itemUIModel.caption, disabled: itemUIModel.disabled ?? this.disabled, endImgSrc: this.useGxRender
                ? fromGxImageToURL(itemUIModel.endImgSrc, this.gxSettings, this.gxImageConstructor)
                : itemUIModel.endImgSrc, endImgType: itemUIModel.endImgType, expandable: expandable, expanded: itemUIModel.expanded, getImagePathCallback: this.getImagePathCallback, inlineAlign: itemUIModel.itemsBlockAlign ?? "outside-end", link: itemUIModel.link, model: itemUIModel, parts: itemUIModel.parts, positionTry: itemUIModel.positionTry ?? this.positionTry, shortcut: itemUIModel.shortcut, startImgSrc: this.useGxRender
                ? fromGxImageToURL(itemUIModel.startImgSrc, this.gxSettings, this.gxImageConstructor)
                : itemUIModel.startImgSrc, startImgType: itemUIModel.startImgType }, expandable &&
            itemUIModel.expanded &&
            this.#renderItems(itemUIModel.items)));
    };
    #renderDictionary = {
        separator: item => (h("hr", { key: item.id, part: tokenMap({
                [item.id]: !!item.id,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.SEPARATOR]: true,
                [item.parts]: !!item.parts
            }) })),
        slot: item => h("slot", { name: item.id })
    };
    #renderItems = (model) => model.map(itemUIModel => actionMenuItemIsActionable(itemUIModel)
        ? this.#renderActionItem(itemUIModel)
        : this.#renderDictionary[itemUIModel.type](
        // TODO: Improve type inference
        itemUIModel));
    #processEvent = (event, type) => {
        const actionMenuInfo = getActionMenuInfoInEvent(event);
        if (actionMenuInfo === undefined) {
            return;
        }
        if (actionMenuInfo === ACTION_MENU_RENDER_TAG_NAME) {
            if (type === "click") {
                return this.expanded ? this.#closeActionMenu() : this.#openActionMenu();
            }
            return;
        }
        const itemUIModel = actionMenuInfo.model;
        // If "click" the event is a PointerEvent
        if (type === "click") {
            // Clicked a hyperlink element
            if (actionMenuItemIsHyperlink(itemUIModel)) {
                const eventInfo = this.hyperlinkClick.emit({
                    item: itemUIModel,
                    event: event
                });
                // Prevent a tag navigation, but don't return so we can close the dropdown
                if (eventInfo.defaultPrevented) {
                    event.preventDefault();
                }
                // TODO: Emit expandedChange event for all element?
                this.#closeActionMenu();
                return;
            }
            // Clicked a button element that is a leaf
            if (!actionMenuItemActionableIsExpandable(itemUIModel)) {
                const eventInfo = this.buttonClick.emit(itemUIModel);
                // Prevent button click and avoid closing the dropdown
                if (eventInfo.defaultPrevented) {
                    event.preventDefault();
                    return;
                }
                // TODO: Emit expandedChange event for all element?
                this.#closeActionMenu();
                return;
            }
        }
        if (type === "mouseout") {
            collapseSubTree(itemUIModel);
            // TODO: Emit expandedChange event
        }
        else {
            collapseAllItems(this.model);
            expandFromRootToNode(itemUIModel);
            const isExpanded = type === "mouseover" || itemUIModel.expanded;
            if (itemUIModel.expanded !== isExpanded) {
                itemUIModel.expanded = isExpanded;
                // Only emit the event if the expanded value was changed
                this.expandedItemChange.emit({
                    item: itemUIModel,
                    expanded: isExpanded
                });
            }
        }
        forceUpdate(this);
    };
    #handleActionMenuItemClick = (event) => this.#processEvent(event, "click");
    #handleActionMenuItemMouseOver = (event) => this.#processEvent(event, "mouseover");
    #handleActionMenuItemMouseOut = (event) => this.#processEvent(event, "mouseout");
    #handleActionMenuKeyDown = (event) => {
        if (!this.expanded &&
            (event.code === KEY_CODES.ARROW_UP || event.code === KEY_CODES.ARROW_DOWN)) {
            this.expanded = true;
            this.expandedChange.emit(true);
            return;
        }
        const keyboardEvent = actionMenuKeyEventsDictionary[event.code];
        if (keyboardEvent) {
            const result = keyboardEvent(event, this.#popoverRef);
            if (!result) {
                return;
            }
            if (result.newExpanded) {
                // TODO: Emit expandedChange event for the collapsed dropdown items
                collapseAllItems(this.model);
                expandFromRootToNode(result.model);
            }
            else {
                collapseSubTree(result.model);
            }
            this.expandedItemChange.emit({
                item: result.model,
                expanded: result.newExpanded
            });
            forceUpdate(this);
        }
    };
    #closeOnClickOutside = (event) => {
        const composedPath = event.composedPath();
        if (!composedPath.includes(this.el)) {
            this.#closeActionMenu();
        }
    };
    #closeOnClickOutsideKeyboard = (event) => {
        if (event.code === KEY_CODES.ESCAPE) {
            this.#actionRef.focus();
            this.#closeActionMenu();
        }
    };
    #openActionMenu = () => {
        this.expanded = true;
        this.expandedChange.emit(true);
    };
    #closeActionMenu = () => {
        collapseAllItems(this.model);
        this.expanded = false;
        this.expandedChange.emit(false);
    };
    #addCloseOnClickOutside = () => {
        document.addEventListener("click", this.#closeOnClickOutside, {
            capture: true,
            passive: true
        });
        document.addEventListener("keydown", this.#closeOnClickOutsideKeyboard, {
            capture: true,
            passive: true
        });
    };
    #removeCloseOnClickOutside = () => {
        document.removeEventListener("click", this.#closeOnClickOutside, true);
        document.removeEventListener("keydown", this.#closeOnClickOutsideKeyboard, true);
    };
    #addMetadataToItems = () => {
        if (this.model) {
            parseSubModel(this.model, undefined);
        }
    };
    connectedCallback() {
        // TODO: Check if this code should be in the constructor
        this.#addMetadataToItems();
        if (this.expanded) {
            this.#addCloseOnClickOutside();
        }
    }
    disconnectedCallback() {
        this.#removeCloseOnClickOutside();
    }
    render() {
        const canAddEventListeners = !(this.disabled && !this.expanded);
        return (
        // TODO: Should we let expand the control if it is disabled? If so, we
        // can't disable the click interaction...
        h(Host, { key: '66cf034b0b2698c52fce03d2026f07283ebb7b55', onClick: canAddEventListeners && this.#handleActionMenuItemClick, onKeyDown: canAddEventListeners && this.#handleActionMenuKeyDown }, h("button", { key: '497b3c98f5868246b758959c7113ccb83f182582', "aria-controls": WINDOW_ID, "aria-expanded": this.expanded.toString(), "aria-haspopup": "true", "aria-label": this.buttonAccessibleName, part: tokenMap({
                [ACTION_MENU_PARTS_DICTIONARY.EXPANDABLE_BUTTON]: true,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.EXPANDED]: this.expanded,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.COLLAPSED]: !this.expanded,
                [ACTION_MENU_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled
            }), disabled: this.disabled, popoverTarget: WINDOW_ID, type: "button", ref: el => (this.#actionRef = el) }, h("slot", { key: '4e114f81b55b2dbc7e887313b0520eb7093ccab5' })), this.expanded && this.model && (h("ch-popover", { key: '95b06265de6ce9bb5bb3791dd9ec171a3e3c05c4', role: "list", id: WINDOW_ID, part: ACTION_MENU_ITEM_PARTS_DICTIONARY.WINDOW, actionById: true,
            // TODO: We must be careful with this property because the control
            // can be expanded on the initial load an the ref will not be
            // correctly computed
            actionElement: this.#actionRef, blockAlign: this.blockAlign, firstLayer: true, inlineAlign: this.inlineAlign, popover: "manual", positionTry: this.positionTry, show: true, onMouseOver: this.#handleActionMenuItemMouseOver, onMouseOut: this.#handleActionMenuItemMouseOut, ref: el => (this.#popoverRef = el) }, this.model !== undefined && this.#renderItems(this.model)))));
    }
    static get is() { return "ch-action-menu-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["action-menu-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["action-menu-render.css"]
        };
    }
    static get properties() {
        return {
            "buttonAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the label for the first expandable button.\nImportant for accessibility."
                },
                "attribute": "button-accessible-name",
                "reflect": false
            },
            "blockAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the block alignment of the dropdown menu that is placed\nrelative to the expandable button."
                },
                "attribute": "block-align",
                "reflect": false,
                "defaultValue": "\"outside-end\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionMenuImagePathCallback",
                    "resolved": "(item: ActionMenuItemActionableModel, iconDirection: \"start\" | \"end\") => GxImageMultiState",
                    "references": {
                        "ActionMenuImagePathCallback": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-menu/types.ts::ActionMenuImagePathCallback"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc or endImgSrc (of an item) needs to be resolved."
                }
            },
            "expanded": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to expand the dropdown window."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "false"
            },
            "gxImageConstructor": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(name: string) => any",
                    "resolved": "(name: string) => any",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                }
            },
            "gxSettings": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "gx-settings",
                "reflect": false
            },
            "inlineAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the inline alignment of the dropdown section that is placed\nrelative to the expandable button."
                },
                "attribute": "inline-align",
                "reflect": false,
                "defaultValue": "\"center\""
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ActionMenuModel | undefined",
                    "resolved": "ActionMenuItemModel[]",
                    "references": {
                        "ActionMenuModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-menu/types.ts::ActionMenuModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the model of the control."
                }
            },
            "positionTry": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"flip-block\" | \"flip-inline\" | \"none\"",
                    "resolved": "\"flip-block\" | \"flip-inline\" | \"none\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies an alternative position to try when the popover overflows the\nwindow."
                },
                "attribute": "position-try",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "useGxRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property is a WA to implement the Tree View as a UC 2.0 in GeneXus."
                },
                "attribute": "use-gx-render",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "buttonClick",
                "name": "buttonClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a button is clicked.\nThis event can be prevented."
                },
                "complexType": {
                    "original": "ActionMenuItemActionableModel",
                    "resolved": "{ id?: string; caption: string; disabled?: boolean; endImgSrc?: string; endImgType?: \"mask\" | \"background\"; expanded?: boolean; items?: ActionMenuModel; itemsBlockAlign?: ChPopoverAlign; itemsInlineAlign?: ChPopoverAlign; link?: ItemLink; parts?: string; positionTry?: \"none\" | \"flip-block\" | \"flip-inline\"; shortcut?: string; startImgSrc?: string; startImgType?: \"mask\" | \"background\"; type?: \"actionable\"; }",
                    "references": {
                        "ActionMenuItemActionableModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-menu/types.ts::ActionMenuItemActionableModel"
                        }
                    }
                }
            }, {
                "method": "expandedChange",
                "name": "expandedChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the visibility of the main dropdown is changed."
                },
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                }
            }, {
                "method": "expandedItemChange",
                "name": "expandedItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the visibility of a dropdown item is changed."
                },
                "complexType": {
                    "original": "ActionMenuExpandedChangeEvent",
                    "resolved": "{ item: ActionMenuItemActionableModel; expanded: boolean; }",
                    "references": {
                        "ActionMenuExpandedChangeEvent": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-menu/types.ts::ActionMenuExpandedChangeEvent"
                        }
                    }
                }
            }, {
                "method": "hyperlinkClick",
                "name": "hyperlinkClick",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an hyperlink is clicked.\nThis event can be prevented, but the dropdown will be closed in any case\n(prevented or not)."
                },
                "complexType": {
                    "original": "ActionMenuHyperlinkClickEvent",
                    "resolved": "{ event: PointerEvent; item: ActionMenuItemActionableModel; }",
                    "references": {
                        "ActionMenuHyperlinkClickEvent": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/action-menu/types.ts::ActionMenuHyperlinkClickEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "expanded",
                "methodName": "expandedChanged"
            }, {
                "propName": "model",
                "methodName": "modelChanged"
            }];
    }
}
